<script setup lang="ts">
//#region import
import { ref } from 'vue';
import UploadFile from '@/components/UploadFile.vue';
import { useApi } from '@/api';
import type { EmitsToListeners, UploadedFile } from '@/types/core';
//#endregion

//#region types
interface Props {
  uploadedFiles?: any[],
  paymentId?: string,
}

interface Emits {
  'success': [],
  'close': [],
}

export interface UploadFilesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  uploadedFiles: () => [],
  paymentId: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(true);
const errors = ref({});

const files = ref<any[]>([]);
const fileList = ref<any[]>([]);
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;

  if (props.uploadedFiles.length > 0) {
    props.uploadedFiles.forEach((file) => {
      fileList.value.push({ name: file.filename, id: file.id });
    });
  }

  isLoading.value = false;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const handleSave = async () => {
  isLoading.value = true;

  try {
    if (fileList.value.length > 0) {
      for (const item of fileList.value) {
        let currentId = item.id || '';
        if(!item.id) {
          const formData = new FormData();
          formData.append('files', item as Blob);
          const response = await api.postFile(formData as unknown as Record<string, File | Blob>, () => {});
          currentId = response.data.payload.files?.id!;
        }
        files.value.push(currentId);
      }
      await api.postPaymentInfoFiles(props.paymentId, files.value);
      emit('success');
      handleClose();
    }
  } catch (e) {

  } finally {
    isLoading.value = false;
  }
};

async function uploadFilesAction(asset: UploadedFile) {
  fileList.value.push(asset);
}

function removeFile(file: UploadedFile) {
  fileList.value = fileList.value.filter((item) => item.uid !== file.uid);
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="upload-files-popup"
    :title="$t(`payments.uploadFilesPopup.title`)"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <UploadFile
        v-model="fileList"
        :title="$t(`payments.uploadFilesPopup.label`)"
        accept=".pdf, .doc, .docx, .png, .jpg, .jpeg"
        is-dragger
        multiple
        :upload-files-action="uploadFilesAction"
        :remove-file="removeFile"
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
